import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus, Search, FileText, Trash2, Edit3, X, Check,
  Printer, Building2, Upload, Settings, Palette,
} from 'lucide-react';
import api from '../utils/api';
import PageHeader from '../components/PageHeader';

// ─── 유틸 ─────────────────────────────────────────────────
function fmt(n) { return Number(n || 0).toLocaleString('ko-KR'); }
function today() { return new Date().toISOString().slice(0, 10); }
function emptyItem() {
  return { group: '', detail: '', spec: '', price: '', qty: 1, unit: '식', count: 1, countUnit: '회', totalPrice: 0, note: '' };
}
function calcItem(item) {
  const p = Number(item.price) || 0, q = Number(item.qty) || 1, c = Number(item.count) || 1;
  return { ...item, totalPrice: p * q * c };
}
function calcTotals(items) {
  const subtotal = items.reduce((s, r) => s + (Number(r.totalPrice) || 0), 0);
  const vat = Math.floor(subtotal * 0.1);
  const total = subtotal + vat;
  return { subtotal, vat, total };
}
function groupSpans(items) {
  const spanMap = {};
  let i = 0;
  while (i < items.length) {
    const g = items[i].group || '';
    let j = i + 1;
    while (j < items.length && (items[j].group || '') === g) j++;
    const info = { start: i, count: j - i, label: g };
    for (let k = i; k < j; k++) spanMap[k] = { ...info, isFirst: k === i };
    i = j;
  }
  return spanMap;
}
function fmtDate(d) {
  if (!d) return '';
  return `${d.slice(0, 4)}년 ${Number(d.slice(5, 7))}월 ${Number(d.slice(8, 10))}일`;
}
// ─── 도장 SVG 생성 (원형, 글자 바둑판 배열) ─────────────
// ─── 도장(인장) 생성 ───────────────────────────────────────
// 예전에는 원 두 개 + 고딕 글자라 "코드로 그린 도형"처럼 보였다.
// 실제로 새긴 도장을 인주로 찍은 느낌을 내려면 형태보다 흔적이 중요하다:
//   · 전각 배열 — 대표자명 3자 + 印 을 2x2 로, 右→左 上→下 로 읽는 전통 배치
//   · 끊긴 테두리 — 완전한 원이 아니라 굵기가 흔들리고 2~3군데 인주가 빈다
//   · 인주 텍스처 — 가장자리 거칠기(feTurbulence+feDisplacementMap),
//     갈라진 보이드, 종이 번짐(약한 블러), 손으로 찍은 미세한 기울기
// 회사명+대표자로 시드를 고정해, 같은 회사는 항상 같은 도장이 나온다.
function sealSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function sealRng(seed) {
  let s = seed || 1;
  return () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; };
}

function sealSvgMarkup(company, sizeAttr) {
  const clean = v => (v || '').replace(/\(주\)|주식회사\s*|㈜/g, '').trim();
  const coN = clean(company?.name), ceoN = clean(company?.ceo);
  const seed = sealSeed(coN + '|' + ceoN);
  const rnd = sealRng(seed);
  const RED = '#c4302b';

  // 대표자명이 있으면 성명인(이름+印), 없으면 회사명 앞 4자
  let chars;
  if (ceoN.length >= 2 && ceoN.length <= 3) chars = [...ceoN, '印'];
  else if (coN.length >= 1) chars = [...coN].slice(0, 4);
  else chars = ['印'];

  let cols, rows;
  if (chars.length === 4) { cols = 2; rows = 2; }
  else if (chars.length === 3) { chars = [...chars, '印']; cols = 2; rows = 2; }
  else if (chars.length === 2) { cols = 2; rows = 1; }
  else { cols = 1; rows = 1; }

  // 오른쪽 열을 위→아래로 먼저 읽는 전통 배열
  const order = (cols === 2 && rows === 2) ? [2, 0, 3, 1] : null;
  const placed = order ? order.map(i => chars[i]) : chars;

  const inset = 26;
  const cellW = (200 - inset * 2) / cols;
  const cellH = (200 - inset * 2) / rows;
  const FF = "'Nanum Myeongjo','Apple SD Gothic Neo','Malgun Gothic',serif";

  const glyphs = placed.map((ch, i) => {
    const cx = inset + cellW * (i % cols) + cellW / 2;
    const cy = inset + cellH * Math.floor(i / cols) + cellH / 2;
    const fs = Math.min(cellW, cellH) * 0.98;
    const sx = Math.min(1.18, (cellW * 0.88) / fs), sy = Math.min(1.18, (cellH * 0.88) / fs);
    const jx = (rnd() - 0.5) * 2.5, jy = (rnd() - 0.5) * 2.5;
    return `<g transform="translate(${(cx + jx).toFixed(1)} ${(cy + jy).toFixed(1)}) scale(${sx.toFixed(3)} ${sy.toFixed(3)})">`
      + `<text x="0" y="0" text-anchor="middle" dominant-baseline="central" font-family="${FF}"`
      + ` font-weight="800" font-size="${fs.toFixed(1)}" fill="${RED}">${ch}</text></g>`;
  }).join('');

  const ringW = 9;
  const rr = 100 - ringW / 2 - 3;
  const gapAt = Array.from({ length: 2 + Math.floor(rnd() * 2) }, () => rnd() * 360);
  let ring = '';
  for (let a = 0; a < 360; a += 6) {
    const near = gapAt.some(g => Math.abs(((a - g + 540) % 360) - 180) > 180 - (6 + rnd() * 10));
    if (near) continue;
    const a0 = (a - 0.6) * Math.PI / 180, a1 = (a + 6.6) * Math.PI / 180;
    const w = ringW * (0.62 + rnd() * 0.55);
    ring += `<path d="M${(100 + rr * Math.cos(a0)).toFixed(1)} ${(100 + rr * Math.sin(a0)).toFixed(1)}`
      + ` A${rr} ${rr} 0 0 1 ${(100 + rr * Math.cos(a1)).toFixed(1)} ${(100 + rr * Math.sin(a1)).toFixed(1)}"`
      + ` fill="none" stroke="${RED}" stroke-width="${w.toFixed(1)}" stroke-linecap="round"`
      + ` opacity="${(0.75 + rnd() * 0.25).toFixed(2)}"/>`;
  }

  const rot = (rnd() - 0.5) * 7;
  const tS = Math.floor(rnd() * 1000);
  const fid = `sealrough${seed.toString(36)}`;   // 한 문서에 여러 도장이 와도 필터 id 충돌 없도록
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"${sizeAttr}>`
    + `<defs><filter id="${fid}" x="-20%" y="-20%" width="140%" height="140%">`
    + `<feTurbulence type="fractalNoise" baseFrequency="0.09" numOctaves="4" seed="${tS}" result="n"/>`
    + `<feDisplacementMap in="SourceGraphic" in2="n" scale="3.2" xChannelSelector="R" yChannelSelector="G" result="d"/>`
    + `<feTurbulence type="fractalNoise" baseFrequency="0.5" numOctaves="3" seed="${tS + 7}" result="n2"/>`
    + `<feColorMatrix in="n2" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1.1 0 0 0 -0.28" result="voids"/>`
    + `<feComposite in="d" in2="voids" operator="out" result="cut"/>`
    + `<feGaussianBlur in="cut" stdDeviation="0.45"/>`
    + `</filter></defs>`
    + `<g transform="rotate(${rot.toFixed(2)} 100 100)" filter="url(#${fid})" opacity="0.92">${ring}${glyphs}</g>`
    + `</svg>`;
}

// 인쇄 HTML에 그대로 박아 넣는 형태 (pt 단위)
function makeSealSvg(company, sizePt = 40) {
  return sealSvgMarkup(company, ` width="${sizePt}pt" height="${sizePt}pt"`);
}

function parseTemplate(t) {
  if (!t) return null;
  if (typeof t === 'object') return t;
  try { return JSON.parse(t); } catch { return null; }
}

// ─── HTML 빌더 ─────────────────────────────────────────────
// 컬러는 회사마다 다른 primaryColor 단 하나, 나머지는 흑/백/회색
const DEFAULT_CFG = {
  primaryColor: '#2563a8', lightColor: '#f0f4fb', darkColor: '#1a3a6b',
  orientation: 'landscape', headerStyle: 'banner', tableStyle: 'filled',
  infoStyle: 'card', fontFamily: "'맑은 고딕','Apple SD Gothic Neo',sans-serif",
  fontBase: '9pt', fontLabel: '11pt', fontAmount: '17pt',
};

// ─── 공통 테이블 헬퍼 ──────────────────────────────────────
function tColgroup() {
  return `<colgroup>
    <col style="width:14%"/><col/>
    <col style="width:8%"/><col style="width:10%"/>
    <col style="width:6%"/><col style="width:5%"/>
    <col style="width:5%"/><col style="width:5%"/>
    <col style="width:12%"/><col style="width:9%"/>
  </colgroup>`;
}

function tHead(cfg) {
  const { primaryColor:P, fontBase:FB } = cfg;
  const cols = ['항목','내역','규격','단가(원)','수량','단위','횟수','단위','산출가','비고'];
  const al = (i) => i===1?'left':[3,8].includes(i)?'right':'center';
  const th = (h,i,ex='') => `<th style="padding:5pt 4pt;font-size:${FB};font-weight:700;text-align:${al(i)};${ex}">${h}</th>`;
  if (cfg.tableStyle === 'filled')
    return `<tr style="background:#1a1a1a;color:#fff;">${cols.map((h,i)=>th(h,i,'border-right:1pt solid #333;font-weight:600;')).join('')}</tr>`;
  if (cfg.tableStyle === 'stripe')
    return `<tr style="background:#4a4a4a;color:#fff;">${cols.map((h,i)=>th(h,i)).join('')}</tr>`;
  if (cfg.tableStyle === 'outlined')
    return `<tr style="border-top:2.5pt solid ${P};">${cols.map((h,i)=>th(h,i,'border:0.5pt solid #d0d0d0;')).join('')}</tr>`;
  return `<tr style="border-bottom:2pt solid #1a1a1a;">${cols.map((h,i)=>th(h,i,'color:#555;')).join('')}</tr>`;
}

function tRows(items, cfg) {
  const { fontBase: FB } = cfg;
  const spanMap = groupSpans(items);
  return items.map((row, idx) => {
    const info = spanMap[idx];
    const groupCell = info?.isFirst
      ? `<td rowspan="${info.count}" style="border-right:1pt solid #e0e0e0;text-align:center;vertical-align:middle;font-size:9pt;font-weight:600;background:#f5f5f5;padding:4pt 5pt;">${info.label}</td>`
      : '';
    const rowBg = cfg.tableStyle === 'stripe' && idx % 2 === 0 ? 'background:#f8f8f8;' : '';
    const border = cfg.tableStyle === 'outlined'
      ? 'border:0.5pt solid #d8d8d8;'
      : 'border-bottom:1pt solid #ebebeb;';
    return `<tr style="${rowBg}">${groupCell}
      <td style="${border}font-size:${FB};padding:4pt 5pt;">${row.detail||''}</td>
      <td style="${border}font-size:${FB};padding:4pt;text-align:center;color:#777;">${row.spec||''}</td>
      <td style="${border}font-size:${FB};padding:4pt 5pt;text-align:right;">${fmt(row.price)}</td>
      <td style="${border}font-size:${FB};padding:4pt;text-align:center;">${row.qty}</td>
      <td style="${border}font-size:${FB};padding:4pt;text-align:center;color:#999;">${row.unit||''}</td>
      <td style="${border}font-size:${FB};padding:4pt;text-align:center;">${row.count}</td>
      <td style="${border}font-size:${FB};padding:4pt;text-align:center;color:#999;">${row.countUnit||'회'}</td>
      <td style="${border}font-size:${FB};padding:4pt 5pt;text-align:right;font-weight:600;">${fmt(row.totalPrice)}</td>
      <td style="${border}font-size:${FB};padding:4pt;text-align:center;color:#bbb;">${row.note||''}</td>
    </tr>`;
  }).join('');
}

function tEmpty(FB) {
  return `<tr><td colspan="10" style="text-align:center;padding:16pt;color:#ccc;font-size:${FB};">품목 없음</td></tr>`;
}

// 합계 행을 표 tbody 마지막에 연결 — style: 'dark'|'line'|'form'|'stripe'
function tTotalsRows(q, P, FB, style) {
  if (style === 'dark') return `
    <tr style="border-top:1.5pt solid #aaa;">
      <td colspan="8" style="text-align:right;padding:4pt 8pt;font-size:${FB};background:#f2f2f2;font-weight:600;color:#666;">소&nbsp;&nbsp;계</td>
      <td style="text-align:right;padding:4pt 8pt;font-size:${FB};background:#f2f2f2;">${fmt(q.subtotal)} 원</td>
      <td style="background:#f2f2f2;"></td>
    </tr><tr>
      <td colspan="8" style="text-align:right;padding:3pt 8pt;font-size:${FB};color:#999;">부가세 (10%)</td>
      <td style="text-align:right;padding:3pt 8pt;font-size:${FB};color:#999;">${fmt(q.vat)} 원</td>
      <td></td>
    </tr><tr>
      <td colspan="8" style="text-align:right;padding:6pt 8pt;font-size:10.5pt;font-weight:700;background:#1a1a1a;color:#fff;letter-spacing:1px;">합&nbsp;&nbsp;계</td>
      <td style="text-align:right;padding:6pt 8pt;font-size:13pt;font-weight:900;background:#1a1a1a;color:${P};white-space:nowrap;">${fmt(q.total)} 원</td>
      <td style="background:#1a1a1a;"></td>
    </tr>`;
  if (style === 'line') return `
    <tr style="border-top:1pt solid #d0d0d0;">
      <td colspan="8" style="text-align:right;padding:3pt 8pt;font-size:${FB};color:#aaa;">소 계</td>
      <td style="text-align:right;padding:3pt 8pt;font-size:${FB};color:#aaa;">${fmt(q.subtotal)}</td>
      <td></td>
    </tr><tr>
      <td colspan="8" style="text-align:right;padding:3pt 8pt;font-size:${FB};color:#bbb;">부가세 (10%)</td>
      <td style="text-align:right;padding:3pt 8pt;font-size:${FB};color:#bbb;">${fmt(q.vat)}</td>
      <td></td>
    </tr><tr style="border-top:2pt solid #1a1a1a;border-bottom:2pt solid #1a1a1a;">
      <td colspan="8" style="text-align:right;padding:5pt 8pt;font-size:11pt;font-weight:700;">총&nbsp;&nbsp;합&nbsp;&nbsp;계</td>
      <td style="text-align:right;padding:5pt 8pt;font-size:14pt;font-weight:900;color:${P};">${fmt(q.total)}</td>
      <td></td>
    </tr>`;
  if (style === 'form') return `
    <tr style="border-top:2pt solid #1a1a1a;">
      <td colspan="5" style="text-align:center;padding:4pt;font-size:${FB};font-weight:600;background:#f5f5f5;border-right:1pt solid #c8c8c8;border-bottom:1pt solid #c8c8c8;">공급가액 합계</td>
      <td colspan="5" style="text-align:right;padding:4pt 8pt;font-size:${FB};border-bottom:1pt solid #c8c8c8;">${fmt(q.subtotal)} 원</td>
    </tr><tr>
      <td colspan="5" style="text-align:center;padding:4pt;font-size:${FB};font-weight:600;background:#f5f5f5;border-right:1pt solid #c8c8c8;border-bottom:1pt solid #c8c8c8;">세액 (10%)</td>
      <td colspan="5" style="text-align:right;padding:4pt 8pt;font-size:${FB};border-bottom:1pt solid #c8c8c8;">${fmt(q.vat)} 원</td>
    </tr><tr>
      <td colspan="5" style="text-align:center;padding:5pt;font-size:10.5pt;font-weight:700;background:#1a1a1a;color:#fff;border-right:1pt solid #333;">합&nbsp;계&nbsp;금&nbsp;액</td>
      <td colspan="5" style="text-align:right;padding:5pt 8pt;font-size:13pt;font-weight:800;color:${P};white-space:nowrap;">${fmt(q.total)} 원</td>
    </tr>`;
  // stripe (diagonal용)
  return `
    <tr style="border-top:2pt solid ${P};">
      <td colspan="8" style="text-align:right;padding:3pt 8pt;font-size:${FB};color:#777;">소 계</td>
      <td style="text-align:right;padding:3pt 8pt;font-size:${FB};color:#777;">${fmt(q.subtotal)} 원</td>
      <td></td>
    </tr><tr>
      <td colspan="8" style="text-align:right;padding:3pt 8pt;font-size:${FB};color:#999;">부가세 (10%)</td>
      <td style="text-align:right;padding:3pt 8pt;font-size:${FB};color:#999;">${fmt(q.vat)} 원</td>
      <td></td>
    </tr><tr style="background:#f8f8f8;border-bottom:2pt solid ${P};">
      <td colspan="8" style="text-align:right;padding:5pt 8pt;font-size:10.5pt;font-weight:700;">최&nbsp;종&nbsp;금&nbsp;액</td>
      <td style="text-align:right;padding:5pt 8pt;font-size:13pt;font-weight:900;color:${P};white-space:nowrap;">${fmt(q.total)} 원</td>
      <td></td>
    </tr>`;
}

// ─── 5가지 레이아웃 (폰트 고정, 영문 없음, 유효기간 없음, 합계 표 연결) ────

// [1] banner — 명조체, 격식 공문서, 합계 표 하단 dark 스타일
function layoutBanner(cfg, co, q, items) {
  const P = cfg.primaryColor;
  const FF = "'Nanum Myeongjo','바탕',Georgia,serif";
  const FB = '9.5pt'; const FL = '11pt'; const FA = '18pt';
  const orientation = cfg.orientation;
  const pageH = orientation === 'landscape' ? '210mm' : '297mm';
  const rcp = [['수신처',q.recipientName],['사업자',q.recipientBizNum],['연락처',q.recipientContact],['주소',q.recipientAddr]].filter(([,v])=>v);
  const cfg2 = {...cfg, fontBase:FB};
  const seal = q._seal;
  const sealTag = seal ? `<img src="${seal}" style="width:22pt;height:22pt;opacity:0.5;vertical-align:middle;margin-left:-3pt;position:relative;top:-1pt;"/>` : '';
  const ceoHtml = co?.ceo ? `${co.ceo}${sealTag}` : sealTag;
  const coInfo = [['사업자',co?.bizNum],['대표자',ceoHtml],['주소',co?.addr],['전화',co?.tel],['업태',co?.bizType],['종목',co?.bizItem]].filter(([,v])=>v);
  return `<!DOCTYPE html><html lang="ko"><head><meta charset="UTF-8"/>
<style>
  @page{size:A4 ${orientation};margin:0}*{box-sizing:border-box;margin:0;padding:0}
  body{font-family:${FF};font-size:${FB};color:#1a1a1a;background:#fff;padding:12mm 14mm;display:flex;flex-direction:column;min-height:${pageH}}
  @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
  table{border-collapse:collapse}.sp{flex:1;min-height:6pt}
</style></head><body>
<div style="text-align:center;margin-bottom:11pt;padding-bottom:9pt;border-bottom:0.5pt solid #ccc;">
  <div style="font-size:25pt;font-weight:900;letter-spacing:14pt;color:#1a1a1a;">견 &nbsp; 적 &nbsp; 서</div>
  <div style="display:flex;align-items:center;justify-content:center;gap:8pt;margin-top:6pt;">
    <div style="flex:1;height:0.5pt;background:#ccc;"></div>
    <div style="width:22pt;height:2.5pt;background:${P};"></div>
    <div style="flex:1;height:0.5pt;background:#ccc;"></div>
  </div>
</div>
<table style="width:100%;border:1pt solid #c8c8c8;margin-bottom:7pt;">
  <colgroup><col style="width:65%"/><col/></colgroup>
  <thead><tr style="background:#f5f5f5;border-bottom:1pt solid #c8c8c8;">
    <th style="padding:4pt 8pt;font-size:7.5pt;color:#888;font-weight:600;text-align:left;border-right:1pt solid #ddd;">공 급 사</th>
    <th style="padding:4pt 8pt;font-size:7.5pt;color:#888;font-weight:600;text-align:left;">견 적 일</th>
  </tr></thead>
  <tbody><tr>
    <td style="padding:6pt 8pt;border-right:1pt solid #e0e0e0;vertical-align:top;">
      <div style="font-size:${FL};font-weight:700;margin-bottom:5pt;">${co?.name||''}</div>
      <div style="display:grid;grid-template-columns:auto 1fr;gap:2pt 8pt;font-size:8pt;color:#555;">
        ${coInfo.map(([k,v])=>`<span style="color:#aaa;white-space:nowrap;">${k}</span><span>${v}</span>`).join('')}
      </div>
    </td>
    <td style="padding:6pt 8pt;color:#555;vertical-align:middle;">${fmtDate(q.quoteDate)}</td>
  </tr></tbody>
</table>
${rcp.length?`<div style="background:#fafafa;border:0.5pt solid #ddd;padding:5pt 10pt;margin-bottom:7pt;display:flex;flex-wrap:wrap;gap:14pt;">
  ${rcp.map(([k,v])=>`<span><span style="color:#aaa;margin-right:4pt;">${k}</span>${v}</span>`).join('')}
</div>`:''}
<table style="width:100%;table-layout:fixed;border:1pt solid #c8c8c8;">
  ${tColgroup()}
  <thead>${tHead(cfg2)}</thead>
  <tbody>
    ${tRows(items,cfg2)||tEmpty(FB)}
    ${tTotalsRows(q,P,FB,'dark')}
  </tbody>
</table>
<div class="sp"></div>
</body></html>`;
}

// [2] sidebar — 돋움체, 진짜 2단, 합계 사이드바 패널 내부
function layoutSidebar(cfg, co, q, items) {
  const P = cfg.primaryColor;
  const FF = "'Dotum','돋움',Arial,sans-serif";
  const FB = '9pt'; const FL = '10.5pt'; const FA = '17pt';
  const orientation = cfg.orientation;
  const pageH = orientation === 'landscape' ? '210mm' : '297mm';
  const cfg2 = {...cfg, fontBase:FB};
  const seal2 = q._seal;
  const sealTag2 = seal2 ? `<img src="${seal2}" style="width:20pt;height:20pt;opacity:0.5;vertical-align:middle;margin-left:-2pt;position:relative;top:-1pt;"/>` : '';
  function kv(k,v) { return v?`<div style="margin-bottom:2.5pt;font-size:8.5pt;"><span style="color:#777;display:inline-block;width:38pt;">${k}</span><span style="color:#ccc;">${v}</span></div>`:''; }
  function kvr(k,v) { return v?`<div style="margin-bottom:2.5pt;font-size:8.5pt;"><span style="color:#777;display:inline-block;width:38pt;">${k}</span><span style="color:#ddd;">${v}</span></div>`:''; }
  return `<!DOCTYPE html><html lang="ko"><head><meta charset="UTF-8"/>
<style>
  @page{size:A4 ${orientation};margin:0}*{box-sizing:border-box;margin:0;padding:0}
  body{font-family:${FF};font-size:${FB};color:#1a1a1a;background:#fff;min-height:${pageH};display:flex;}
  @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
  table{border-collapse:collapse}
</style></head><body>
<div style="width:150pt;flex-shrink:0;background:#1e1e1e;color:#ddd;padding:12mm 11pt;display:flex;flex-direction:column;">
  <div style="font-size:${FA};font-weight:700;color:#fff;letter-spacing:3pt;margin-bottom:8pt;">견적서</div>
  <div style="width:100%;height:1pt;background:#333;margin-bottom:9pt;"></div>
  <div style="font-size:7.5pt;color:#666;margin-bottom:4pt;">공 급 사</div>
  <div style="font-size:${FL};font-weight:700;color:#fff;margin-bottom:2pt;">${co?.name||''}</div>
  ${(co?.ceo||sealTag2)?`<div style="margin-bottom:2.5pt;font-size:8.5pt;"><span style="color:#777;display:inline-block;width:38pt;">대표자</span><span style="color:#ccc;">${co?.ceo||''}${sealTag2}</span></div>`:''}${kv('사업자',co?.bizNum)}${kv('업태',co?.bizType)}${kv('종목',co?.bizItem)}${kv('주소',co?.addr)}${kv('전화',co?.tel)}
  <div style="width:100%;height:1pt;background:#333;margin:9pt 0 8pt;"></div>
  <div style="font-size:7.5pt;color:#666;margin-bottom:4pt;">수 신 처</div>
  ${kvr('상호',q.recipientName)}${kvr('사업자',q.recipientBizNum)}${kvr('연락처',q.recipientContact)}
  ${kvr('주소',q.recipientAddr)}
  <div style="flex:1;min-height:10pt;"></div>
  <div style="width:100%;height:1pt;background:#333;margin-bottom:9pt;"></div>
  <div style="font-size:7.5pt;color:#666;margin-bottom:5pt;">견 적 금 액</div>
  <div style="font-size:8.5pt;color:#888;margin-bottom:2pt;">소 계 &nbsp; ${fmt(q.subtotal)} 원</div>
  <div style="font-size:8.5pt;color:#888;margin-bottom:7pt;">부가세 &nbsp; ${fmt(q.vat)} 원</div>
  <div style="font-size:${FA};font-weight:800;color:${P};line-height:1;">${fmt(q.total)}</div>
  <div style="font-size:8pt;color:#888;margin-top:2pt;">원 (VAT 포함)</div>
  <div style="margin-top:9pt;width:22pt;height:2pt;background:${P};"></div>
</div>
<div style="flex:1;padding:12mm 13pt;display:flex;flex-direction:column;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:9pt;padding-bottom:7pt;border-bottom:1pt solid #e0e0e0;">
    <div style="font-size:${FL};font-weight:700;">${co?.name||''}</div>
    <div style="text-align:right;font-size:${FB};color:#888;line-height:1.9;">
      <div>${fmtDate(q.quoteDate)}</div>
    </div>
  </div>
  <table style="width:100%;table-layout:fixed;">
    ${tColgroup()}
    <thead>${tHead(cfg2)}</thead>
    <tbody>${tRows(items,cfg2)||tEmpty(FB)}</tbody>
  </table>
  <div style="flex:1;min-height:6pt;"></div>
</div>
</body></html>`;
}

// [3] minimal — Courier New 모노스페이스, 합계 표 하단 line 스타일
function layoutMinimal(cfg, co, q, items) {
  const P = cfg.primaryColor;
  const FF = "'Courier New','Lucida Console',monospace";
  const FB = '8.5pt'; const FL = '10pt'; const FA = '19pt';
  const orientation = cfg.orientation;
  const pageH = orientation === 'landscape' ? '210mm' : '297mm';
  const seal = q._seal;
  const sealTag = seal ? `<img src="${seal}" style="width:20pt;height:20pt;opacity:0.5;vertical-align:middle;margin-left:-2pt;position:relative;top:-1pt;"/>` : '';
  const ceoHtml = co?.ceo ? `${co.ceo}${sealTag}` : sealTag;
  const rcp = [[q.recipientName,'수신처'],[q.recipientBizNum,'사업자'],[q.recipientContact,'연락처']].filter(([v])=>v);
  const sup = [[co?.name,'상호'],[ceoHtml,'대표자'],[co?.bizNum,'사업자'],[co?.bizType,'업태'],[co?.bizItem,'종목'],[co?.addr,'주소'],[co?.tel,'전화']].filter(([v])=>v);
  const spanMap = groupSpans(items);
  const rows = items.map((row,idx)=>{
    const info=spanMap[idx];
    const gc=info?.isFirst?`<td rowspan="${info.count}" style="border-bottom:1pt solid #eee;padding:4pt 5pt;text-align:center;font-weight:700;color:#555;">${info.label}</td>`:'';
    return `<tr style="border-bottom:1pt solid #eee;">${gc}
      <td style="padding:4pt 5pt;">${row.detail||''}</td>
      <td style="padding:4pt;text-align:center;color:#aaa;">${row.spec||''}</td>
      <td style="padding:4pt 5pt;text-align:right;">${fmt(row.price)}</td>
      <td style="padding:4pt;text-align:center;">${row.qty}</td>
      <td style="padding:4pt;text-align:center;color:#aaa;">${row.unit||''}</td>
      <td style="padding:4pt;text-align:center;">${row.count}</td>
      <td style="padding:4pt;text-align:center;color:#aaa;">${row.countUnit||'회'}</td>
      <td style="padding:4pt 5pt;text-align:right;font-weight:700;">${fmt(row.totalPrice)}</td>
      <td style="padding:4pt;text-align:center;color:#ccc;">${row.note||''}</td>
    </tr>`;
  }).join('') || tEmpty(FB);
  return `<!DOCTYPE html><html lang="ko"><head><meta charset="UTF-8"/>
<style>
  @page{size:A4 ${orientation};margin:0}*{box-sizing:border-box;margin:0;padding:0}
  body{font-family:${FF};font-size:${FB};color:#1a1a1a;background:#fff;padding:14mm 16mm;display:flex;flex-direction:column;min-height:${pageH}}
  @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
  table{border-collapse:collapse}
</style></head><body>
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:9pt;">
  <div>
    <div style="font-size:${FA};font-weight:900;letter-spacing:-1pt;line-height:1;">${co?.name||''}</div>
  </div>
  <div style="text-align:right;">
    <div style="font-size:21pt;font-weight:900;letter-spacing:4pt;">견적서</div>
    <div style="font-size:${FB};color:#aaa;margin-top:1pt;">${fmtDate(q.quoteDate)}</div>
  </div>
</div>
<div style="height:2pt;background:#1a1a1a;margin-bottom:2pt;"></div>
<div style="height:0.5pt;background:#e0e0e0;margin-bottom:10pt;"></div>
<div style="display:grid;grid-template-columns:1fr 1fr;gap:20pt;margin-bottom:11pt;padding-bottom:9pt;border-bottom:1pt dashed #ddd;">
  <div>
    <div style="font-size:7pt;color:#ccc;margin-bottom:5pt;letter-spacing:1pt;">수 신 처</div>
    ${rcp.map(([v,k])=>`<div style="font-size:${FB};margin-bottom:2pt;"><span style="color:#bbb;margin-right:6pt;">${k}</span>${v}</div>`).join('')}
    ${q.recipientAddr?`<div style="font-size:${FB};color:#bbb;margin-top:2pt;">${q.recipientAddr}</div>`:''}
  </div>
  <div>
    <div style="font-size:7pt;color:#ccc;margin-bottom:5pt;letter-spacing:1pt;">공 급 사</div>
    ${sup.map(([v,k])=>`<div style="font-size:${FB};margin-bottom:2pt;"><span style="color:#bbb;margin-right:6pt;">${k}</span>${v}</div>`).join('')}
  </div>
</div>
<table style="width:100%;table-layout:fixed;">
  ${tColgroup()}
  <thead><tr style="border-top:1pt solid #1a1a1a;border-bottom:1pt solid #ccc;">
    ${['항목','내역','규격','단가(원)','수량','단위','횟수','단위','산출가','비고'].map((h,i)=>
    `<th style="padding:4pt 5pt;font-size:${FB};font-weight:700;color:#888;text-align:${i===1?'left':[3,8].includes(i)?'right':'center'};">${h}</th>`).join('')}
  </tr></thead>
  <tbody>
    ${rows}
    ${tTotalsRows(q,P,FB,'line')}
  </tbody>
</table>
<div style="flex:1;min-height:6pt;"></div>
</body></html>`;
}

// [4] split — 굴림체, 전통 한국 공문서식, 합계도 격자형(form 스타일)
function layoutSplit(cfg, co, q, items) {
  const P = cfg.primaryColor;
  const FF = "'Gulim','굴림',Arial,sans-serif";
  const FB = '9pt';
  const orientation = cfg.orientation;
  const pageH = orientation === 'landscape' ? '210mm' : '297mm';
  const cfg2 = {...cfg, fontBase:FB};

  const hasRealImage = q._seal && (q._seal.startsWith('data:image/jpeg') || q._seal.startsWith('data:image/png') || q._seal.startsWith('data:image/gif') || q._seal.startsWith('data:image/webp'));
  const sealEl = hasRealImage
    ? `<img src="${q._seal}" style="width:26pt;height:26pt;opacity:0.85;vertical-align:middle;"/>`
    : makeSealSvg(co, 26);

  const LBL = `background:#f0f0f0;font-size:${FB};font-weight:600;padding:3pt 5pt;border:1pt solid #b8b8b8;text-align:center;white-space:nowrap;`;
  const VAL = `font-size:${FB};padding:3pt 6pt;border:1pt solid #b8b8b8;word-break:break-all;overflow-wrap:break-word;`;
  const SEP = `border-right:1.5pt solid #1a1a1a;`;

  function cell(label, value) {
    return `<td style="${LBL}">${label}</td><td style="${VAL}">${value || ''}</td>`;
  }

  const ceoRow = co?.ceo
    ? `<tr><td style="${LBL}">대표자</td><td style="${VAL}overflow:visible;"><div style="display:inline-block;position:relative;">${co.ceo}<div style="position:absolute;left:100%;top:50%;margin-top:-13pt;margin-left:3pt;line-height:0;">${sealEl}</div></div></td></tr>`
    : '';

  const recipientRows = [
    q.recipientName    ? `<tr>${cell('상호',   q.recipientName)}</tr>`    : '',
    q.recipientBizNum  ? `<tr>${cell('사업자', q.recipientBizNum)}</tr>`  : '',
    q.recipientContact ? `<tr>${cell('연락처', q.recipientContact)}</tr>` : '',
    q.recipientAddr    ? `<tr><td colspan="2" style="${VAL}color:#666;">${q.recipientAddr}</td></tr>` : '',
  ].join('') || `<tr><td colspan="2" style="height:52pt;"></td></tr>`;

  const companyRows = [
    co?.name    ? `<tr>${cell('상호',   co.name)}</tr>`    : '',
    ceoRow,
    co?.bizNum  ? `<tr>${cell('사업자', co.bizNum)}</tr>`  : '',
    co?.bizType ? `<tr>${cell('업태',   co.bizType)}</tr>` : '',
    co?.bizItem ? `<tr>${cell('종목',   co.bizItem)}</tr>` : '',
    co?.addr    ? `<tr>${cell('소재지', co.addr)}</tr>`    : '',
    co?.tel     ? `<tr>${cell('전화',   co.tel)}</tr>`     : '',
  ].join('');

  return `<!DOCTYPE html><html lang="ko"><head><meta charset="UTF-8"/>
<style>
  @page{size:A4 ${orientation};margin:0}*{box-sizing:border-box;margin:0;padding:0}
  body{font-family:${FF};font-size:${FB};color:#1a1a1a;background:#fff;padding:12mm 12mm;display:flex;flex-direction:column;min-height:${pageH}}
  @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
  table{border-collapse:collapse}.sp{flex:1;min-height:6pt}
</style></head><body>

<table style="width:100%;table-layout:fixed;border:2pt solid #1a1a1a;margin-bottom:8pt;">
  <colgroup><col width="12.5%"><col width="12.5%"><col width="12.5%"><col width="12.5%"><col width="12.5%"><col width="12.5%"><col width="12.5%"><col width="12.5%"></colgroup>

  <tr>
    <td colspan="8" style="text-align:center;padding:10pt 0;border-bottom:1.5pt solid #1a1a1a;">
      <div style="font-size:22pt;font-weight:900;letter-spacing:14pt;">견 &nbsp;&nbsp;&nbsp; 적 &nbsp;&nbsp;&nbsp; 서</div>
    </td>
  </tr>

  <tr>
    <td colspan="4" style="padding:0;vertical-align:top;${SEP}">
      <div style="font-size:7.5pt;font-weight:700;padding:3pt 7pt;background:#e8e8e8;border-bottom:1pt solid #c8c8c8;letter-spacing:1pt;">공급받는자</div>
      <table style="width:100%;table-layout:fixed;border-collapse:collapse;"><col width="28%"><col width="72%">${recipientRows}</table>
    </td>
    <td colspan="4" style="padding:0;vertical-align:top;position:relative;">
      <div style="font-size:7.5pt;font-weight:700;padding:3pt 7pt;background:#e8e8e8;border-bottom:1pt solid #c8c8c8;letter-spacing:1pt;">공급자</div>
      <table style="width:100%;table-layout:fixed;border-collapse:collapse;"><col width="28%"><col width="72%">${companyRows}</table>
    </td>
  </tr>

  <tr style="border-top:1.5pt solid #1a1a1a;">
    <td colspan="2" style="${LBL}">견적일자</td>
    <td colspan="6" style="${VAL}">${fmtDate(q.quoteDate)}</td>
  </tr>
</table>

<table style="width:100%;table-layout:fixed;border:1pt solid #b8b8b8;">
  ${tColgroup()}
  <thead>${tHead(cfg2)}</thead>
  <tbody>
    ${tRows(items,cfg2)||tEmpty(FB)}
    ${tTotalsRows(q,P,FB,'form')}
  </tbody>
</table>
<div class="sp"></div>
</body></html>`;
}

// [5] diagonal — Georgia 세리프, 모던 인보이스, 합계 표 하단 stripe 스타일
function layoutDiagonal(cfg, co, q, items) {
  const P = cfg.primaryColor;
  const FF = "'Georgia','Times New Roman',serif";
  const FB = '9.5pt'; const FL = '11pt'; const FA = '18pt';
  const orientation = cfg.orientation;
  const pageH = orientation === 'landscape' ? '210mm' : '297mm';
  const seal = q._seal;
  const sealTag = seal ? `<img src="${seal}" style="width:22pt;height:22pt;opacity:0.5;vertical-align:middle;margin-left:-3pt;position:relative;top:-1pt;"/>` : '';
  const ceoHtml = co?.ceo ? `${co.ceo}${sealTag}` : sealTag;
  const cfg2 = {...cfg, fontBase:FB};
  const sup = [[ceoHtml,'대표자'],[co?.bizNum,'사업자'],[co?.bizType,'업태'],[co?.bizItem,'종목'],[co?.addr,'주소'],[co?.tel,'전화']].filter(([v])=>v);
  return `<!DOCTYPE html><html lang="ko"><head><meta charset="UTF-8"/>
<style>
  @page{size:A4 ${orientation};margin:0}*{box-sizing:border-box;margin:0;padding:0}
  body{font-family:${FF};font-size:${FB};color:#1a1a1a;background:#fff;padding:12mm 14mm;display:flex;flex-direction:column;min-height:${pageH}}
  @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
  table{border-collapse:collapse}.sp{flex:1;min-height:6pt}
</style></head><body>
<div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:7pt;gap:16pt;">
  <div style="flex:1;">
    <div style="font-size:${FL};font-weight:700;margin-bottom:5pt;">${co?.name||''}</div>
    ${sup.length?`<div style="display:grid;grid-template-columns:auto 1fr;gap:2pt 10pt;font-size:8.5pt;">
      ${sup.map(([v,k])=>`<span style="color:#bbb;white-space:nowrap;">${k}</span><span style="color:#555;">${v}</span>`).join('')}
    </div>`:''}
  </div>
  <div style="text-align:right;flex-shrink:0;">
    <div style="font-size:${FA};font-weight:900;letter-spacing:6pt;">견적서</div>
    <div style="font-size:${FB};color:#bbb;margin-top:1pt;">${fmtDate(q.quoteDate)}</div>
  </div>
</div>
<div style="height:3pt;background:${P};margin-bottom:2pt;"></div>
<div style="height:0.5pt;background:#e0e0e0;margin-bottom:9pt;"></div>
<div style="border-left:3pt solid ${P};padding:7pt 12pt;background:#fafafa;margin-bottom:8pt;">
  <div style="font-size:7pt;color:#aaa;margin-bottom:4pt;letter-spacing:1pt;">수 신 처</div>
  ${q.recipientName?`<div style="font-size:${FL};font-weight:700;margin-bottom:3pt;">${q.recipientName}</div>`:''}
  ${[[q.recipientBizNum,'사업자'],[q.recipientContact,'연락처'],[q.recipientAddr,'주소']].filter(([v])=>v).map(([v,k])=>
  `<div style="font-size:${FB};color:#777;margin-bottom:1.5pt;"><span style="color:#bbb;display:inline-block;width:40pt;white-space:nowrap;">${k}</span>${v}</div>`).join('')}
</div>
<table style="width:100%;table-layout:fixed;">
  ${tColgroup()}
  <thead>${tHead(cfg2)}</thead>
  <tbody>
    ${tRows(items,cfg2)||tEmpty(FB)}
    ${tTotalsRows(q,P,FB,'stripe')}
  </tbody>
</table>
<div class="sp"></div>
</body></html>`;
}

// ─── 디스패처 ──────────────────────────────────────────────
function buildCompetitorHtml(quote, company, sealDataUri) {
  const cfg = { ...DEFAULT_CFG, ...(parseTemplate(company?.template) || {}) };
  const items = Array.isArray(quote.items) ? quote.items : [];
  const { subtotal, vat, total } = calcTotals(items);
  const q = { ...quote, subtotal, vat, total, _seal: sealDataUri || '' };

  switch (cfg.headerStyle) {
    case 'banner':   return layoutBanner(cfg, company, q, items);
    case 'sidebar':  return layoutSidebar(cfg, company, q, items);
    case 'minimal':  return layoutMinimal(cfg, company, q, items);
    case 'split':    return layoutSplit(cfg, company, q, items);
    default:         return layoutDiagonal(cfg, company, q, items);
  }
}

// ─── 미리보기: 인쇄 HTML을 그대로 iframe으로 렌더링 ────────
function CompetitorPreview({ quote, company, sealDataUri }) {
  const items = Array.isArray(quote.items) ? quote.items : [];
  const { subtotal, vat, total } = calcTotals(items);
  const html = buildCompetitorHtml({ ...quote, subtotal, vat, total }, company, sealDataUri, items.length);
  return (
    <iframe
      key={company?.id}
      srcDoc={html}
      className="w-full border-0 bg-white rounded-xl"
      style={{ minHeight: 640, flex: 1 }}
      title="미리보기"
    />
  );
}

// ─── 품목 행 ─────────────────────────────────────────────
function ItemRow({ item, idx, onChange, onRemove, isSameGroupAsPrev }) {
  function up(field, val) { onChange(idx, calcItem({ ...item, [field]: val })); }
  const base = "w-full bg-transparent border border-transparent rounded px-1.5 py-1 text-xs text-gray-100 focus:outline-none focus:border-brand/50";
  return (
    <tr className={`border-b border-gray-800/60 group ${isSameGroupAsPrev ? 'border-l-2 border-l-brand/30' : ''}`}>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.group||''} onChange={e=>up('group',e.target.value)} placeholder="항목" className={base+' text-center font-medium'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.detail||''} onChange={e=>up('detail',e.target.value)} placeholder="내역" className={base} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.spec||''} onChange={e=>up('spec',e.target.value)} placeholder="-" className={base+' text-center'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input type="number" value={item.price||''} onChange={e=>up('price',e.target.value)} placeholder="0" className={base+' text-right'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input type="number" value={item.qty||1} onChange={e=>up('qty',e.target.value)} className={base+' text-center'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.unit||''} onChange={e=>up('unit',e.target.value)} placeholder="식" className={base+' text-center'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input type="number" value={item.count||1} onChange={e=>up('count',e.target.value)} className={base+' text-center'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.countUnit||'회'} onChange={e=>up('countUnit',e.target.value)} className={base+' text-center'} />
      </td>
      <td className="px-2 py-0.5 text-right text-xs font-semibold text-brand whitespace-nowrap group-hover:bg-brand/[0.04]">
        {fmt(item.totalPrice)}
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.note||''} onChange={e=>up('note',e.target.value)} placeholder="-" className={base+' text-center'} />
      </td>
      <td className="px-1 py-0.5 text-center group-hover:bg-brand/[0.04]">
        <button onClick={()=>onRemove(idx)} className="text-gray-700 hover:text-red-400 transition-colors"><X size={13}/></button>
      </td>
    </tr>
  );
}

// ─── 기존 견적서 불러오기 모달 ────────────────────────────
// 견적서 / 타견적서 중 아무거나 골라 그 내용을 새 타견적서로 가져온다.
// 두 소스의 수신처·품목 필드 이름이 같아서 같은 handleImport 로 처리된다.
const IMPORT_SOURCES = {
  quote: {
    label: '견적서',
    listUrl: '/quotations',
    detailUrl: id => `/quotations/${id}`,
    empty: '기존 견적서가 없습니다',
  },
  competitor: {
    label: '타견적서',
    listUrl: '/competitor-quotes',
    detailUrl: id => `/competitor-quotes/${id}`,
    empty: '기존 타견적서가 없습니다',
  },
};

function ImportQuoteModal({ onImport, onClose }) {
  const [source, setSource] = useState('quote');
  const [lists, setLists] = useState({});        // 탭별로 캐시 — 오갈 때 다시 안 부른다
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (lists[source]) { setLoading(false); return; }
    setLoading(true);
    api.get(IMPORT_SOURCES[source].listUrl)
      .then(r => setLists(p => ({ ...p, [source]: r.data })))
      .catch(() => setLists(p => ({ ...p, [source]: [] })))
      .finally(() => setLoading(false));
  }, [source, lists]);

  async function handleSelect(item) {
    try {
      const { data } = await api.get(IMPORT_SOURCES[source].detailUrl(item.id));
      onImport(data);
      onClose();
    } catch { alert('불러오지 못했습니다.'); }
  }

  const list = lists[source] || [];
  const filtered = list.filter(q =>
    !search ||
    (q.recipientName||'').includes(search) ||
    (q.quoteNumber||'').includes(search) ||
    (q.companyName||'').includes(search)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-[480px] max-h-[70vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 flex-shrink-0">
          <h3 className="font-semibold text-white text-sm flex items-center gap-2">
            <Upload size={15}/> 기존 문서 불러오기
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16}/></button>
        </div>
        {/* 소스 선택 */}
        <div className="px-4 pt-3 flex-shrink-0">
          <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-0.5">
            {Object.entries(IMPORT_SOURCES).map(([key, cfg]) => (
              <button key={key} onClick={() => { setSource(key); setSearch(''); }}
                className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  source === key ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
                {cfg.label}
              </button>
            ))}
          </div>
        </div>
        <div className="px-4 py-3 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center bg-gray-800 border border-gray-700 rounded-lg px-2.5">
            <Search size={13} className="text-gray-500 flex-shrink-0"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="수신처 또는 번호 검색..."
              className="flex-1 bg-transparent text-white text-xs py-2 px-2 focus:outline-none placeholder-gray-600"/>
            {search && <button onClick={()=>setSearch('')} className="text-gray-600 hover:text-gray-400"><X size={12}/></button>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="py-10 text-center text-xs text-gray-500">불러오는 중...</div>}
          {!loading && filtered.length === 0 && (
            <div className="py-10 text-center text-xs text-gray-600">
              {search ? '검색 결과 없음' : IMPORT_SOURCES[source].empty}
            </div>
          )}
          {!loading && filtered.map(q => (
            <button key={q.id} onClick={() => handleSelect(q)}
              className="w-full text-left px-5 py-3 hover:bg-gray-800 border-b border-gray-800/60 last:border-0 transition-colors">
              <div className="flex items-center gap-1.5">
                {q.companyName && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300 flex-shrink-0">{q.companyName}</span>
                )}
                <span className="font-medium text-gray-100 text-sm truncate">{q.recipientName || '수신처 없음'}</span>
              </div>
              <div className="text-gray-500 text-xs mt-0.5">{q.quoteNumber} · {q.quoteDate} · {fmt(q.total)}원</div>
            </button>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-gray-800 flex-shrink-0 flex justify-end">
          <button onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 transition-colors">
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportQuoteButton({ onImport }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-700 rounded-lg text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
        <Upload size={13}/> 불러오기
      </button>
      {open && createPortal(<ImportQuoteModal onImport={onImport} onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}

// ─── 회사 관리 모달 ───────────────────────────────────────
function DesignBadge({ template }) {
  const cfg = parseTemplate(template);
  if (!cfg) return null;
  const style = { backgroundColor: cfg.primaryColor + '22', color: cfg.primaryColor, borderColor: cfg.primaryColor + '44' };
  const label = `${cfg.orientation === 'landscape' ? '가로' : '세로'} · ${cfg.headerStyle} · ${cfg.tableStyle}`;
  return (
    <span style={style} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium">
      <span style={{ width:7, height:7, borderRadius:'50%', background: cfg.primaryColor, display:'inline-block', flexShrink:0 }}/>
      {label}
    </span>
  );
}

function CompanyModal({ companies, onClose, onChanged }) {
  const [list, setList] = useState(companies);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sealFile, setSealFile] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const inp = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/50";

  function startNew() {
    setEditing('new');
    setSealFile(null);
    setForm({ name:'', bizNum:'', ceo:'', addr:'', bizType:'', bizItem:'', tel:'', bankInfo:'' });
  }
  function startEdit(c) {
    setEditing(c);
    setSealFile(null);
    setForm({ name:c.name, bizNum:c.bizNum||'', ceo:c.ceo||'', addr:c.addr||'', bizType:c.bizType||'', bizItem:c.bizItem||'', tel:c.tel||'', bankInfo:c.bankInfo||'' });
  }

  async function save() {
    if (!form.name?.trim()) { alert('회사명을 입력하세요.'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        const { data } = await api.post('/competitor-companies', form);
        let company = data;
        if (sealFile) {
          const fd = new FormData();
          fd.append('seal', sealFile);
          const { data: sd } = await api.post(`/competitor-companies/${data.id}/seal`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          company = { ...data, sealImageUrl: sd.sealImageUrl };
          setSealFile(null);
        }
        const updated = [...list, company];
        setList(updated);
        onChanged(updated);
        setEditing(null);
      } else {
        const { data } = await api.put(`/competitor-companies/${editing.id}`, form);
        const updated = list.map(c => c.id === editing.id ? data : c);
        setList(updated);
        onChanged(updated);
        setEditing(null);
      }
    } catch (e) { alert('저장 실패: ' + (e.response?.data?.message || e.message)); }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('이 회사를 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/competitor-companies/${id}`);
      const updated = list.filter(c => c.id !== id);
      setList(updated);
      onChanged(updated);
      if (editing?.id === id) setEditing(null);
    } catch (e) { alert('삭제 실패: ' + (e.response?.data?.message || e.message)); }
  }

  async function uploadSeal(company) {
    if (!sealFile) return;
    setUploadingId(company.id);
    try {
      const fd = new FormData();
      fd.append('seal', sealFile);
      const { data } = await api.post(`/competitor-companies/${company.id}/seal`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const updated = list.map(c => c.id === company.id ? { ...c, sealImageUrl: data.sealImageUrl } : c);
      setList(updated);
      onChanged(updated);
      setSealFile(null);
    } catch (e) { alert('업로드 실패: ' + (e.response?.data?.message || e.message)); }
    setUploadingId(null);
  }

  const currentCompany = editing !== 'new' ? list.find(c => c.id === editing?.id) || editing : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-[780px] max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 flex-shrink-0">
          <h3 className="font-semibold text-white text-sm flex items-center gap-2"><Building2 size={16}/> 회사 관리</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16}/></button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* 목록 */}
          <div className="w-60 border-r border-gray-800 flex flex-col">
            <div className="p-3 border-b border-gray-800">
              <button onClick={startNew}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-brand text-white text-xs rounded-lg hover:bg-brand/80">
                <Plus size={13}/> 회사 추가
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {list.length === 0 && <div className="p-4 text-xs text-gray-600 text-center">등록된 회사 없음</div>}
              {list.map(c => (
                <button key={c.id} onClick={()=>startEdit(c)}
                  className={`w-full text-left px-3 py-2.5 border-b border-gray-800/50 transition-colors ${editing?.id===c.id?'bg-brand/20':'hover:bg-gray-800'}`}>
                  <div className="text-sm font-medium text-white truncate">{c.name}</div>
                  {c.bizItem && <div className="text-xs text-gray-500 mt-0.5">{c.bizItem}</div>}
                </button>
              ))}
            </div>
          </div>

          {/* 편집 폼 */}
          <div className="flex-1 overflow-y-auto p-5">
            {!editing ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-600 gap-2">
                <Building2 size={32} className="opacity-30"/>
                <p className="text-sm">회사를 선택하거나 추가하세요</p>
                <p className="text-xs text-gray-700">새 회사 등록 시 고유 디자인이 자동 생성됩니다</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {[['회사명 *','name','(주)OO기획'],['사업자번호','bizNum','000-00-00000'],['대표자','ceo','홍길동'],['전화','tel','02-0000-0000'],['업태','bizType','서비스'],['업종','bizItem','공연기획업']].map(([lbl,key,ph])=>(
                    <div key={key}>
                      <label className="block text-xs text-gray-500 mb-1">{lbl}</label>
                      <input value={form[key]||''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} placeholder={ph} className={inp}/>
                    </div>
                  ))}
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">주소</label>
                    <input value={form.addr||''} onChange={e=>setForm(f=>({...f,addr:e.target.value}))} placeholder="서울시 OO구 OO로 000" className={inp}/>
                  </div>
                </div>

                {/* 도장 이미지 */}
                <div className="border border-gray-700 rounded-xl p-4">
                  <label className="block text-xs font-medium text-gray-400 mb-3">
                    도장 이미지
                    {editing === 'new' && <span className="ml-2 text-gray-600 font-normal">(저장 시 함께 업로드)</span>}
                  </label>
                  <div className="flex items-center gap-4">
                    {editing !== 'new' && currentCompany?.sealImageUrl ? (
                      <img src={currentCompany.sealImageUrl} alt="도장" className="w-16 h-16 object-contain border border-gray-700 rounded-lg"/>
                    ) : sealFile ? (
                      <img src={URL.createObjectURL(sealFile)} alt="미리보기" className="w-16 h-16 object-contain border border-gray-700 rounded-lg"/>
                    ) : (
                      <div className="w-16 h-16 border border-dashed border-gray-700 rounded-lg flex items-center justify-center text-gray-600 text-xs">없음</div>
                    )}
                    <div className="flex-1">
                      <input type="file" accept="image/*" onChange={e=>setSealFile(e.target.files[0])}
                        className="text-xs text-gray-300 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:bg-gray-700 file:text-gray-300 file:text-xs hover:file:bg-gray-600 cursor-pointer"/>
                      {sealFile && editing !== 'new' && (
                        <button onClick={()=>uploadSeal(currentCompany||editing)} disabled={!!uploadingId}
                          className="mt-2 px-3 py-1.5 bg-brand text-white text-xs rounded-lg hover:bg-brand/80 disabled:opacity-50">
                          {uploadingId?'업로드 중...':'업로드'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  {editing !== 'new' ? (
                    <button onClick={()=>handleDelete(editing.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-red-900/50 text-xs text-red-500 hover:bg-red-900/20 rounded-lg transition-colors">
                      <Trash2 size={13}/> 삭제
                    </button>
                  ) : <div/>}
                  <div className="flex gap-2">
                    <button onClick={()=>setEditing(null)} className="px-4 py-1.5 text-sm text-gray-500 border border-gray-700 rounded-lg hover:border-gray-500 transition-colors">취소</button>
                    <button onClick={save} disabled={saving}
                      className="flex items-center gap-1.5 px-5 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80 disabled:opacity-50">
                      <Check size={14}/>{saving?'저장 중...':'저장'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 타견적서 폼 ──────────────────────────────────────────
function CompetitorForm({ initial, companies, onSave, onCancel, onPrint }) {
  const [form, setForm] = useState(() => ({
    companyId: initial?.companyId || (companies[0]?.id ?? null),
    quoteNumber: initial?.quoteNumber || '',
    recipientName: initial?.recipientName || '',
    recipientBizNum: initial?.recipientBizNum || '',
    recipientAddr: initial?.recipientAddr || '',
    recipientContact: initial?.recipientContact || '',
    quoteDate: initial?.quoteDate || today(),
    validDays: initial?.validDays ?? 7,
    items: Array.isArray(initial?.items) && initial.items.length > 0 ? initial.items : [emptyItem()],
    status: initial?.status || 'draft',
  }));
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [sealDataUri, setSealDataUri] = useState('');

  const selectedCompany = companies.find(c => c.id === form.companyId) || companies[0];
  const cfg = parseTemplate(selectedCompany?.template);

  // 회사 변경 또는 신규 시 회사별 채번 요청
  useEffect(() => {
    if (!initial?.quoteNumber && form.companyId) {
      api.get('/competitor-quotes/next-number', { params: { companyId: form.companyId } })
        .then(r => setForm(f => ({ ...f, quoteNumber: r.data.number }))).catch(() => {});
    }
  }, [initial?.quoteNumber, form.companyId]);

  useEffect(() => {
    setSealDataUri('');
    const url = selectedCompany?.sealImageUrl;
    if (!url) return;
    fetch(url).then(r=>r.blob())
      .then(blob => new Promise(resolve => { const r=new FileReader(); r.onload=()=>resolve(r.result); r.readAsDataURL(blob); }))
      .then(setSealDataUri).catch(()=>{});
  }, [selectedCompany?.id, selectedCompany?.sealImageUrl]);

  function handleImport(q) {
    setForm(f => ({
      ...f,
      recipientName: q.recipientName||'',
      recipientBizNum: q.recipientBizNum||'',
      recipientAddr: q.recipientAddr||'',
      recipientContact: q.recipientContact||'',
      // 원본과 객체를 공유하면 여기서 고친 값이 원본 화면에도 비치므로 행마다 복사
      items: Array.isArray(q.items) && q.items.length > 0 ? q.items.map(it => ({ ...it })) : [emptyItem()],
    }));
  }

  function upItems(idx, updated) { setForm(f=>({...f,items:f.items.map((it,i)=>i===idx?updated:it)})); }
  function addItem() { setForm(f=>({...f,items:[...f.items,emptyItem()]})); }
  function removeItem(idx) { setForm(f=>({...f,items:f.items.filter((_,i)=>i!==idx)})); }

  const { subtotal, vat, total } = calcTotals(form.items);
  const inp = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/50";

  async function submit() {
    if (!form.companyId) { alert('회사를 선택해주세요.'); return; }
    if (!form.quoteDate) { alert('날짜를 입력해주세요.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, subtotal, vat, total };
      if (initial?.id) {
        await api.put(`/competitor-quotes/${initial.id}`, payload);
      } else {
        const { data } = await api.post('/competitor-quotes', payload);
        payload.id = data.id;
      }
      onSave({ ...initial, ...payload, company: selectedCompany, companyName: selectedCompany?.name });
    } catch (e) {
      alert('저장 실패: ' + (e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  }

  // 디자인 색상 힌트
  const accentColor = cfg?.primaryColor || '#d4005e';
  const accentStyle = { color: accentColor };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 px-5 pt-4 pb-3 border-b border-gray-800 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-0.5 w-fit">
            <button onClick={()=>setPreview(false)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${!preview?'bg-gray-700 text-white':'text-gray-500 hover:text-gray-300'}`}>편집</button>
            <button onClick={()=>setPreview(true)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${preview?'bg-gray-700 text-white':'text-gray-500 hover:text-gray-300'}`}>미리보기</button>
          </div>
          <ImportQuoteButton onImport={handleImport}/>
        </div>

        <div className="flex items-center gap-2">
          <Building2 size={14} className="text-gray-500 flex-shrink-0"/>
          <span className="text-xs text-gray-500 flex-shrink-0">공급자:</span>
          <select value={form.companyId||''} onChange={e=>setForm(f=>({...f,companyId:Number(e.target.value)}))}
            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-100 focus:outline-none focus:border-brand/50">
            {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {cfg && (
            <span style={{ background: accentColor+'18', color: accentColor, borderColor: accentColor+'44' }}
              className="text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0">
              고유 디자인 적용됨
            </span>
          )}
          {companies.length === 0 && <span className="text-xs text-red-400">먼저 회사를 등록하세요</span>}
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        {preview ? (
          <CompetitorPreview
            quote={{ ...form, subtotal, vat, total }}
            company={selectedCompany}
            sealDataUri={sealDataUri}
          />
        ) : (
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">날짜</label>
                <input type="date" value={form.quoteDate} onChange={e=>setForm(f=>({...f,quoteDate:e.target.value}))}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-100 focus:outline-none focus:border-brand/50 w-40"/>
              </div>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 space-y-3">
              <span className="text-sm font-semibold text-gray-300">수신처</span>
              <div className="grid grid-cols-2 gap-3">
                {[['상호명','recipientName','업체명'],['사업자번호','recipientBizNum','000-00-00000'],['연락처','recipientContact','이메일 또는 전화']].map(([lbl,key,ph])=>(
                  <div key={key}>
                    <label className="block text-xs text-gray-500 mb-1">{lbl}</label>
                    <input value={form[key]||''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} placeholder={ph} className={inp}/>
                  </div>
                ))}
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">주소</label>
                  <input value={form.recipientAddr||''} onChange={e=>setForm(f=>({...f,recipientAddr:e.target.value}))} placeholder="주소" className={inp}/>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-300">품목</span>
                <button onClick={addItem} className="flex items-center gap-1 text-xs text-brand hover:opacity-80"><Plus size={13}/> 행 추가</button>
              </div>
              <div className="overflow-x-auto border border-gray-700 rounded-xl">
                <table style={{ minWidth:900, width:'100%', tableLayout:'fixed', borderCollapse:'collapse' }} className="text-xs">
                  <colgroup>
                    <col style={{width:116}}/><col/>
                    <col style={{width:60}}/><col style={{width:86}}/>
                    <col style={{width:48}}/><col style={{width:44}}/>
                    <col style={{width:48}}/><col style={{width:44}}/>
                    <col style={{width:88}}/><col style={{width:106}}/>
                    <col style={{width:28}}/>
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-800 border-b border-gray-700">
                      {[['항목','center'],['내역','left'],['규격','center'],['단가(원)','right'],['수량','center'],['단위','center'],['횟수','center'],['단위','center'],['산출가','right'],['비고','center'],['','center']].map(([h,a],i)=>(
                        <th key={i} className="py-2 px-1.5 font-semibold text-gray-500 text-[11px] whitespace-nowrap" style={{textAlign:a}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item,idx)=>(
                      <ItemRow key={idx} item={item} idx={idx} onChange={upItems} onRemove={removeItem}
                        isSameGroupAsPrev={idx>0&&(item.group||'')===(form.items[idx-1].group||'')&&!!item.group}/>
                    ))}
                    {form.items.length===0&&<tr><td colSpan={11} className="py-6 text-center text-xs text-gray-600">품목을 추가하세요</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end">
              <table className="text-sm border-collapse">
                <tbody>
                  {[['합 계',fmt(subtotal)+' 원',false],['부가세 (10%)',fmt(vat)+' 원',false],['최종 견적',fmt(total)+' 원',true]].map(([lbl,val,bold])=>(
                    <tr key={lbl}>
                      <td className="bg-gray-800 border border-gray-700 px-5 py-1.5 font-medium text-gray-400 text-right whitespace-nowrap">{lbl}</td>
                      <td className={`border border-gray-700 px-5 py-1.5 text-right min-w-28 ${bold?'font-bold text-brand text-base':'text-gray-300'}`}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        )}
      </div>

      <div className="flex-shrink-0 border-t border-gray-800 px-5 py-3 flex items-center justify-between">
        <div>
          {preview && (
            <button onClick={()=>onPrint({ ...form, subtotal, vat, total }, selectedCompany, sealDataUri)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
              <Printer size={13}/> 인쇄
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="px-5 py-2 rounded-lg text-sm text-gray-500 hover:text-gray-300 border border-gray-800 hover:border-gray-700 transition-colors">취소</button>
          <button onClick={submit} disabled={saving}
            className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm font-semibold text-white bg-brand hover:bg-brand/80 disabled:opacity-50 transition-colors">
            <Check size={14}/>{saving?'저장 중...':'저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 메인 ─────────────────────────────────────────────────
export default function CompetitorQuotePage() {
  const [quoteList, setQuoteList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('idle');
  const [search, setSearch] = useState('');
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [sealCache, setSealCache] = useState({});

  const loadAll = useCallback(async (autoSelect = false) => {
    setLoading(true);
    try {
      const [qRes, cRes] = await Promise.all([api.get('/competitor-quotes'), api.get('/competitor-companies')]);
      setQuoteList(qRes.data);
      setCompanies(cRes.data);
      if (autoSelect && qRes.data.length > 0) {
        const { data } = await api.get(`/competitor-quotes/${qRes.data[0].id}`);
        setSelected(data);
        setMode('view');
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(true); }, [loadAll]);

  async function loadDetail(id) {
    try { const { data } = await api.get(`/competitor-quotes/${id}`); setSelected(data); setMode('view'); } catch {}
  }

  async function handleDelete(id) {
    if (!window.confirm('이 타견적서를 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/competitor-quotes/${id}`);
      setSelected(null); setMode('idle'); loadAll();
    } catch (e) { alert('삭제 실패: ' + (e.response?.data?.message || e.message)); }
  }

  function handleSaved(q) { setSelected(q); setMode('view'); loadAll(); }

  // 회사에 등록된 도장 이미지가 없을 때 쓰는 기본 도장.
  // 인쇄용 makeSealSvg 와 같은 생성기를 쓰므로 미리보기/인쇄가 동일하다.
  function generateSealSvg(company) {
    const svg = sealSvgMarkup(company, '');
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  }

  function getSealDataUri(company) {
    const url = company?.sealImageUrl;
    if (!url) return generateSealSvg(company);
    if (sealCache[company.id]) return sealCache[company.id];
    fetch(url).then(r=>r.blob())
      .then(blob=>new Promise(resolve=>{ const r=new FileReader(); r.onload=()=>resolve(r.result); r.readAsDataURL(blob); }))
      .then(uri=>setSealCache(c=>({...c,[company.id]:uri}))).catch(()=>{});
    return generateSealSvg(company);
  }

  function handlePrint(quote, company, sealDataUri) {
    const items = Array.isArray(quote.items) ? quote.items : [];
    const win = window.open('', '_blank', 'width=1000,height=760');
    if (!win) { alert('팝업이 차단되었습니다.'); return; }
    win.document.write(buildCompetitorHtml(quote, company, sealDataUri, items.length));
    win.document.close();
    setTimeout(() => win.print(), 600);
  }

  function handleViewPrint() {
    const company = companies.find(c=>c.id===selected.companyId) || selected.company;
    handlePrint(selected, company, getSealDataUri(company));
  }

  const filtered = quoteList.filter(q => {
    if (!search) return true;
    return (q.recipientName||'').includes(search)||(q.quoteNumber||'').includes(search)||(q.companyName||'').includes(search);
  });

  return (
    <div className="flex flex-col h-full">
      <PageHeader icon={Building2} title="타견적서" count={quoteList.length}>
        <button onClick={()=>setShowCompanyModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-700 rounded-lg text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
          <Settings size={13}/> 회사 관리
        </button>
        <button onClick={()=>{ setSelected(null); setMode('new'); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
          <Plus size={14}/> 타견적서 추가
        </button>
      </PageHeader>

      <div className="flex flex-1 min-h-0 gap-4 overflow-hidden">
        <div className="w-64 flex-shrink-0 flex flex-col gap-3">
          <div className="flex items-center bg-gray-900 border border-gray-800 rounded-xl px-2.5">
            <Search size={13} className="text-gray-500 flex-shrink-0"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="검색..."
              className="flex-1 bg-transparent text-white text-xs py-2 px-2 focus:outline-none placeholder-gray-600"/>
            {search && <button onClick={()=>setSearch('')} className="text-gray-600 hover:text-gray-400"><X size={12}/></button>}
          </div>
          <div className="flex-1 overflow-y-auto flex flex-col gap-1">
            {loading && <div className="text-center py-8 text-xs text-gray-600">불러오는 중...</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-8 text-xs text-gray-600">{search?'검색 결과 없음':'타견적서를 추가하세요'}</div>
            )}
            {filtered.map(q => {
              const co = companies.find(c=>c.id===q.companyId);
              const cfg = parseTemplate(co?.template);
              const accentColor = cfg?.primaryColor || '#888';
              return (
                <button key={q.id} onClick={()=>loadDetail(q.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all ${selected?.id===q.id&&mode==='view'?'bg-brand/20 border-brand/30':'border-transparent hover:bg-gray-800'}`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span style={{ background: accentColor+'22', color: accentColor, borderColor: accentColor+'44' }}
                      className="text-[10px] px-1.5 py-0.5 rounded border font-medium truncate max-w-[90px]">
                      {q.companyName || '회사 없음'}
                    </span>
                  </div>
                  <div className="text-xs font-medium text-white truncate mt-0.5">{q.recipientName||'수신처 미입력'}</div>
                  <div className="text-[10px] text-gray-500">{q.quoteDate}{q.authorName ? ` · ${q.authorName}` : ''}</div>
                  <div className="text-sm font-semibold text-brand mt-0.5">{fmt(q.total)}원</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-w-0 overflow-hidden">
          {mode === 'idle' && (
            <div className="flex flex-col items-center justify-center h-full text-gray-600 gap-3">
              <FileText size={40} className="opacity-30"/>
              <p className="text-sm">타견적서를 선택하거나 새로 작성하세요.</p>
              {companies.length === 0 && (
                <button onClick={()=>setShowCompanyModal(true)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
                  <Building2 size={14}/> 회사 먼저 등록하기
                </button>
              )}
            </div>
          )}

          {(mode==='new'||mode==='edit') && (
            <CompetitorForm
              initial={mode==='edit' ? selected : null}
              companies={companies}
              onSave={handleSaved}
              onCancel={()=>setMode(selected?'view':'idle')}
              onPrint={handlePrint}
            />
          )}

          {mode==='view' && selected && (() => {
            const company = companies.find(c=>c.id===selected.companyId)||selected.company;
            const cfg = parseTemplate(company?.template);
            const accentColor = cfg?.primaryColor || '#d4005e';
            return (
              <div className="flex flex-col h-full overflow-hidden">
                <div className="flex items-center justify-between px-2 py-2 flex-shrink-0 border-b border-gray-800/60">
                  <div className="flex items-center gap-2">
                    <span style={{ background: accentColor+'22', color: accentColor, borderColor: accentColor+'44' }}
                      className="text-[11px] px-2 py-0.5 rounded border font-medium">
                      {company?.name||selected.companyName||'회사 없음'}
                    </span>
                    <span className="text-xs text-gray-600">{selected.quoteNumber}{selected.authorName ? ` · 작성 ${selected.authorName}` : ''}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={handleViewPrint} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                      <Printer size={13}/> 인쇄
                    </button>
                    <button onClick={()=>setMode('edit')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                      <Edit3 size={13}/> 수정
                    </button>
                    <button onClick={()=>handleDelete(selected.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-900/50 text-xs text-red-500 hover:bg-red-900/20 transition-colors">
                      <Trash2 size={13}/> 삭제
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-hidden flex">
                  <CompetitorPreview
                    quote={selected}
                    company={company}
                    sealDataUri={getSealDataUri(company)}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {showCompanyModal && (
        <CompanyModal
          companies={companies}
          onClose={()=>setShowCompanyModal(false)}
          onChanged={updated=>setCompanies(updated)}
        />
      )}
    </div>
  );
}
